"use client";

import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion"; // ensuring motion is used
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Megaphone,
  FileText,
  CheckCircle2,
  Eye,
  Clock,
  ChevronRight,
  ShieldCheck,
  Building2,
  AlertTriangle,
} from "lucide-react";

// Register GSAP
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const NoticeSection = () => {
  const containerRef = useRef(null);
  const [isNoticeOpen, setIsNoticeOpen] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  // --- ANIMATIONS ---
  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 70%",
          toggleActions: "play none none reverse",
        },
      });

      // Header Animation (GSAP)
      tl.from(".notice-header", {
        y: 30,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
      })
        // NOTE: Removed .feature-item animation from here to prevent visibility lock.
        // We are using Framer Motion for the list items instead.
        .from(
          ".notice-visual",
          {
            scale: 0.95,
            opacity: 0,
            duration: 0.8,
            ease: "back.out(1.2)",
          },
          "-=0.2",
        );
    },
    { scope: containerRef },
  );

  return (
    <section
      ref={containerRef}
      // FIX: Standardized padding for S-Tier look (px-6 mobile, px-20 desktop)
      className="py-24 bg-[#050505] px-6 md:px-18  relative overflow-hidden"
    >
      {/* Background Ambience */}
      <div className="absolute top-1/2 right-10 w-[600px] h-[600px] bg-orange-900/10 blur-[150px] rounded-full pointer-events-none" />

      {/* FIX: Increased gap to gap-16 to prevent mobile overlap */}
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center relative z-10">
        {/* LEFT: TEXT CONTENT */}
        {/* FIX: Changed order-1 so Text appears FIRST on mobile */}
        {/* FIX: Removed 'top-10' class to fix alignment */}
        <div className="space-y-8 text-center lg:text-left order-1 lg:order-1">
          <div className="notice-header">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-400 text-[10px] font-bold uppercase tracking-widest mb-6 backdrop-blur-md">
              <Megaphone className="w-3 h-3" /> Communication Module
            </div>
            <h2 className="text-5xl md:text-5xl font-clash font-bold text-white mb-4 leading-tight">
              Official Notices. <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                Legally Acknowledged.
              </span>
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed max-w-xl mx-auto lg:mx-0">
              Stop pasting papers in elevators. Send official,
              letterhead-formatted notices directly to residents&apos; phones
              and track exactly who read them.
            </p>
          </div>

          <div className="space-y-6 max-w-lg mx-auto lg:mx-0">
            {[
              {
                title: "Auto-Letterhead Format",
                desc: "Notices are automatically rendered on the society's official digital letterhead.",
                icon: FileText,
              },
              {
                title: "Read Receipt Tracking",
                desc: "Know exactly who opened the notice and who ignored it. 100% auditable.",
                icon: Eye,
              },
              {
                title: "Digital Acknowledgement",
                desc: "Residents click to acknowledge, creating a legal timestamp in the database.",
                icon: ShieldCheck,
              },
            ].map((item, i) => (
              // FIX: Switched to motion.div to ensure visibility
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="flex flex-col md:flex-row gap-4 p-4 hover:border-orange-500/20 hover:border rounded-2xl border border-white/5 bg-[#111] hover:bg-white/5 transition-all duration-300 group text-left"
              >
                <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center group-hover:scale-110 transition-transform group-hover:ease-in-out shrink-0 mx-auto md:mx-0">
                  <item.icon className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h4 className="text-white font-bold mb-1 text-center md:text-left">
                    {item.title}
                  </h4>
                  <p className="text-sm text-gray-500 text-center md:text-left">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* RIGHT: INTERACTIVE VISUAL */}
        {/* FIX: Changed order-2 so Phone appears SECOND on mobile */}
        <div className="notice-visual relative flex items-center justify-center order-2 lg:order-2">
          {/* Phone Container */}
          {/* FIX: Responsive height h-[600px] md:h-[700px] */}
          <div className="relative w-full max-w-[360px] bg-[#000] border-[6px] border-[#1a1a1a] rounded-[3rem] shadow-2xl overflow-hidden h-[600px] md:h-[700px]">
            {/* Dynamic Island */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[#1a1a1a] rounded-b-xl z-20 flex justify-center items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-800" />
              <div className="w-12 h-1.5 rounded-full bg-gray-800/50" />
            </div>

            {/* App Header */}
            <div className="pt-12 pb-6 px-6 flex justify-between items-center bg-[#080808]">
              <div>
                <h3 className="font-bold text-white text-lg">Notice Board</h3>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">
                  Gokuldham CHS
                </p>
              </div>
              <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Megaphone className="w-4 h-4 text-orange-500" />
              </div>
            </div>

            {/* List of Notices (Background Layer) */}
            <div className="mt-2 space-y-4 px-4">
              {/* Background Card */}
              <div className="p-5 rounded-2xl bg-[#111] border border-white/5 opacity-40 scale-95 origin-bottom">
                <div className="flex justify-between mb-2">
                  <span className="text-[10px] bg-green-900/20 text-green-500 px-2 py-0.5 rounded font-bold">
                    MAINTENANCE
                  </span>
                  <span className="text-[10px] text-gray-600">2d ago</span>
                </div>
                <h4 className="font-bold text-gray-500">Lift Maintenance</h4>
              </div>

              {/* The Active Card */}
              <motion.div
                layoutId="notice-card"
                onClick={() => setIsNoticeOpen(true)}
                className="p-5 rounded-3xl bg-[#111] border border-orange-500/30 cursor-pointer relative overflow-hidden group shadow-lg shadow-orange-900/10 hover:border-orange-500 transition-colors"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent pointer-events-none" />
                <div className="flex justify-between mb-3 relative z-10">
                  <span className="text-[10px] bg-red-500/20 text-red-500 border border-red-500/30 px-2 py-0.5 rounded font-bold tracking-wider flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> URGENT
                  </span>
                  <span className="text-[10px] text-orange-200/60 font-mono">
                    Today, 10:00 AM
                  </span>
                </div>
                <h4 className="font-bold text-xl text-white mb-2 relative z-10">
                  AGM Meeting Notice
                </h4>
                <p className="text-sm text-gray-400 truncate relative z-10">
                  Mandatory attendance for all members...
                </p>

                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between relative z-10">
                  {!isAcknowledged ? (
                    <div className="flex items-center gap-2 text-[10px] text-orange-400 font-bold animate-pulse">
                      <Clock className="w-3 h-3" /> ACTION REQUIRED
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-[10px] text-green-500 font-bold">
                      <CheckCircle2 className="w-3 h-3" /> ACKNOWLEDGED
                    </div>
                  )}
                  <span className="text-[10px] text-gray-500 flex items-center gap-1 group-hover:text-white transition-colors">
                    Read Full <ChevronRight className="w-3 h-3" />
                  </span>
                </div>
              </motion.div>

              {/* Background Card 2 */}
              <div className="p-5 rounded-2xl bg-[#111] border border-white/5 opacity-40 scale-90 origin-top">
                <div className="flex justify-between mb-2">
                  <span className="text-[10px] bg-purple-900/20 text-purple-500 px-2 py-0.5 rounded font-bold">
                    EVENT
                  </span>
                  <span className="text-[10px] text-gray-600">1w ago</span>
                </div>
                <h4 className="font-bold text-gray-500">Diwali Photos</h4>
              </div>
            </div>

            {/* 2. The Expanded Letterhead Modal */}
            <AnimatePresence>
              {isNoticeOpen && (
                <motion.div
                  layoutId="notice-card"
                  className="absolute inset-0 bg-[#f8f9fa] z-30 flex flex-col text-black overflow-hidden"
                >
                  {/* Paper Texture Overlay */}
                  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cardboard-flat.png')] opacity-10 pointer-events-none" />

                  {/* Modal Header */}
                  <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white shadow-sm relative z-10">
                    <button
                      onClick={() => setIsNoticeOpen(false)}
                      className="text-orange-500 font-bold text-xs flex items-center gap-1 hover:text-black"
                    >
                      <ChevronRight className="w-4 h-4 rotate-180" /> Back
                    </button>
                    <span className="text-[10px] uppercase font-bold text-orange-400">
                      Preview Mode
                    </span>
                  </div>

                  {/* Letterhead Content */}
                  <div className="flex-1 p-6 overflow-y-auto relative">
                    {/* Society Header */}
                    <div className="text-center border-b-2 border-black pb-4 mb-6">
                      <div className="flex justify-center mb-2">
                        <Building2 className="w-8 h-8 text-black" />
                      </div>
                      <h2 className="font-serif font-bold text-xl uppercase tracking-wider">
                        Gokuldham CHS Ltd.
                      </h2>
                      <p className="text-[10px] text-gray-600 font-bold">
                        Reg No: BOM/HSG/1234 • Goregaon East, Mumbai
                      </p>
                    </div>

                    {/* Notice Body */}
                    <div className="space-y-4">
                      <div className="flex justify-between items-end">
                        <h3 className="font-bold text-lg underline underline-offset-4">
                          NOTICE
                        </h3>
                        <p className="text-xs font-mono">Date: 10 Feb 2026</p>
                      </div>

                      <p className="text-sm font-bold">
                        Subject: Annual General Meeting (AGM)
                      </p>

                      <p className="text-xs leading-relaxed text-gray-800 text-justify">
                        Dear Residents,
                        <br />
                        <br />
                        Notice is hereby given that the Annual General Meeting
                        of the society will be held on{" "}
                        <strong>Sunday, 25th Feb 2026</strong> at 10:00 AM in
                        the Clubhouse.
                        <br />
                        <br />
                        <strong>Agenda:</strong>
                        <br />
                        1. Adoption of accounts for FY 2025-26.
                        <br />
                        2. Discussion on major repairs.
                        <br />
                        3. Appointment of Auditors.
                        <br />
                        <br />
                        Attendance is mandatory.
                      </p>

                      <div className="pt-8 flex justify-end">
                        <div className="text-center">
                          {/* Signature */}
                          <div
                            className="font-script text-xl text-black mb-1 opacity-80"
                            style={{ fontFamily: "cursive" }}
                          >
                            A.Bhide
                          </div>
                          <div className="h-px w-24 bg-black mb-1" />
                          <p className="text-[10px] font-bold uppercase text-gray-500">
                            Hon. Secretary
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sticky Action Footer */}
                  <div className="p-4 bg-white border-t border-gray-200 shadow-[0_-5px_20px_rgba(0,0,0,0.05)] relative z-10">
                    {!isAcknowledged ? (
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsAcknowledged(true);
                        }}
                        className="w-full py-3 bg-orange-500 text-white font-bold rounded-lg shadow-lg flex items-center justify-center gap-2 text-sm"
                      >
                        <ShieldCheck className="w-4 h-4" /> I Acknowledge
                      </motion.button>
                    ) : (
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="w-full py-3 bg-green-50 text-green-700 border border-green-200 font-bold rounded-lg flex items-center justify-center gap-2 text-sm"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Recorded on{" "}
                        {new Date().toLocaleDateString()}
                      </motion.div>
                    )}
                  </div>

                  {/* Stamped Effect Overlay */}
                  <AnimatePresence>
                    {isAcknowledged && (
                      <motion.div
                        initial={{ scale: 2, opacity: 0, rotate: -12 }}
                        animate={{ scale: 1, opacity: 0.15, rotate: -12 }}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border-[6px] border-green-900 p-4 rounded-xl pointer-events-none z-0"
                      >
                        <span className="text-6xl font-black text-green-900 uppercase tracking-widest">
                          RECEIVED
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NoticeSection;
